import React, { useState, useEffect } from 'react';
import PageHeader from '../../components/PageHeader/PageHeader';
import DefaultCard from '../../components/default/DefaultCard';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import DefaultModal from '../../components/default/DefaultModal';
import InputField from '../../components/InputField/InputField';
import SelectField from '../../components/SelectField/SelectField';
import ButtonSaveForm from '../../components/buttons/ButtonSaveForm';
import ButtonCancelForm from '../../components/buttons/ButtonCancelForm';
import ForeignExchangeDataTable from './components/ForeignExchangeDataTable';
import useCurrencyService from '../../app/services/hooks/useCurrencyService';
import useHandleErrorFields from '../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';



const ForeignExchange: React.FC = () => {
    const { getCurrencies, getForeignExchanges, storeExchangeManually, fetchingGetCurrencies, fetchingGetExchanges, fetchingStoreExchange } = useCurrencyService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    
    interface Currency {
        id: number;
        name: string;
    }

    interface Exchange {
        id: number;
        currency: string;
        name?: string;
        value: number;
        exchange_date: string;
    }

    const [currencies, setCurrencies] = useState<Currency[]>([]);
    const [exchanges, setExchanges] = useState<Exchange[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    
    const [filter, setFilter] = useState({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });
    
    const [formData, setFormData] = useState({
        currency_id: '',
        value: '',
        exchange_date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        loadCurrencies();
        loadExchanges();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const loadCurrencies = () => {
        getCurrencies({
            onSuccess: (response: ServiceResponse) => {
                if (response.data && response.data.currencies) {
                    setCurrencies(response.data.currencies);
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al cargar monedas');
            }
        });
    };

    const loadExchanges = () => {
        const filters = { page: filter.page, per_page: filter.per_page };
        getForeignExchanges(filters, {
            onSuccess: (response: ServiceResponse) => {
                if (response.data) {
                    setExchanges(response.data.foreign_exchanges || []);
                    setTotalRows(response.data.total_rows || 0);
                }
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message || 'Error al cargar valores de conversión');
            }
        });
    };

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({ ...prev, [field]: value.toString() }));
    };

    const handleOpenModal = () => {
        setIsEditing(false);
        setFormData({
            currency_id: '',
            value: '',
            exchange_date: new Date().toISOString().split('T')[0]
        });
        setErrors({});
        setShowModal(true);
    };

    const handleEdit = (exchange: Exchange) => {
        setIsEditing(true);
        
        // Buscar el currency_id basado en el nombre de la moneda
        const currency = currencies.find(c => c.name === exchange.currency);
        
        setFormData({
            currency_id: currency?.id?.toString() || '',
            value: exchange.value?.toString() || '',
            exchange_date: exchange.exchange_date ? new Date(exchange.exchange_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        });
        setErrors({});
        setShowModal(true);
    };

    const handleDelete = () => {
        useReactConfirmAlert().requestConfirmation({
            title: '¿Eliminar valor?',
            message: 'Esta acción no se puede deshacer. ¿Está seguro que desea continuar?',
            buttons: {
                confirmButton: {
                    label: 'Sí, eliminar',
                    onClick: () => {
                        // TODO: Implementar servicio de delete
                        toast.success('Valor eliminado correctamente');
                        loadExchanges();
                    }
                },
                cancelButton: {
                    label: 'Cancelar',
                    onClick: () => {
                        // No hacer nada
                    }
                }
            }
        });
    };

    const handleSubmit = () => {
        setErrors({});
        
        storeExchangeManually(formData, {
            onSuccess: (response: ServiceResponse) => {
                toast.success(response.message || 'Valor agregado correctamente');
                setShowModal(false);
                setFormData({
                    currency_id: '',
                    value: '',
                    exchange_date: new Date().toISOString().split('T')[0]
                });
                loadExchanges();
            },
            onError: (response: ServiceResponse) => {
                if (response.status === 'fields_validation' && response.data) {
                    setErrors(response.data);
                    toast.error('Por favor corrige los errores en el formulario');
                } else {
                    toast.error(response.message || 'Error al agregar valor');
                }
            }
        });
    };

    return (
        <div className="container-fluid">
            <PageHeader
                breadcrumbs={[
                    { label: 'Inicio', path: '/intranet' },
                    { label: 'Configuración', isCurrent: false },
                    { label: 'Conversión de Monedas', isCurrent: true }
                ]}
                title="Conversión de Monedas"
                description="Valores de UTM, UF, dólar y euro que usa la plataforma para calcular tasas y precios. Se actualizan solos cada mañana; aquí puedes corregirlos."
            />

            <div className="row">
                <div className="col-12">
                    <DefaultCard>
                        <div className="d-flex justify-content-end align-items-center mb-4">
                            <ButtonCreate title="Agregar Valor" callbackCreate={handleOpenModal} />
                        </div>

                        <ForeignExchangeDataTable
                            exchanges={exchanges}
                            totalRows={totalRows}
                            loading={fetchingGetExchanges}
                            edit={handleEdit}
                            destroy={handleDelete}
                            filter={filter}
                            setFilter={setFilter}
                        />
                    </DefaultCard>
                </div>
            </div>

            {/* Modal Agregar/Editar Valor */}
            <DefaultModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                title={isEditing ? 'Editar Valor de Conversión' : 'Agregar Valor de Conversión'}
                showFooter={false}
            >
                <div className="mb-4">
                    <SelectField
                        label="Moneda"
                        value={formData.currency_id}
                        onChange={(value) => handleInputChange('currency_id', value)}
                        onFocus={() => onFocusRemove('currency_id')}
                        placeholder="Seleccione una moneda"
                        disabled={fetchingGetCurrencies}
                        error={fieldErrorMessage('currency_id')}
                        required
                    >
                        {currencies.map((c: Currency) => (
                            <option key={c.id} value={c.id.toString()}>
                                {c.name}
                            </option>
                        ))}
                    </SelectField>
                </div>

                <div className="mb-4">
                    <InputField
                        label="Valor (en CLP)"
                        type="number"
                        value={formData.value}
                        onChange={(value) => handleInputChange('value', value)}
                        onFocus={() => onFocusRemove('value')}
                        placeholder="Ej: 37000"
                        error={fieldErrorMessage('value')}
                        required
                    />
                </div>

                <div className="mb-4">
                    <InputField
                        label="Fecha"
                        type="date"
                        value={formData.exchange_date}
                        onChange={(value) => handleInputChange('exchange_date', value)}
                        onFocus={() => onFocusRemove('exchange_date')}
                        error={fieldErrorMessage('exchange_date')}
                        required
                    />
                </div>

                <hr />
                <div className="row justify-content-end">
                    <div className="col-auto">
                        <ButtonCancelForm callbackCancel={() => setShowModal(false)} />
                    </div>
                    <div className="col-auto">
                        <ButtonSaveForm
                            callbackSave={handleSubmit}
                            locked={fetchingStoreExchange}
                        />
                    </div>
                </div>
            </DefaultModal>
        </div>
    );
};

export default ForeignExchange;

